import { type EnvironmentProviders, provideAppInitializer } from '@angular/core';
import {
  LUMIR_ENTRY_COOKIE,
  LUMIR_ENTRY_SOURCES,
  LUMIR_ENTRY_TEXT_MAX_CHARS,
  type LumirEntryCookie,
  type LumirEntrySource,
  type LumirEntryTextKey,
} from '@lite-workspace/dto-common';

import { readCookie, writeCookie } from './first-party-cookie';

export interface LumirEntry extends LumirEntryCookie {
  at: string;
}

export interface LumirEntryContext {
  referrer: string;
  search: string;
  hostname: string;
}

const ENTRY_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 2;
const PARENT_DOMAIN = 'lumir.photo';
const LANDING_HOSTS: readonly string[] = ['lumir.photo', 'www.lumir.photo'];
// A return from sign-in, ours or a social provider's, says nothing about where the visitor came from.
const SIGN_IN_HOSTS: readonly string[] = ['id.lumir.photo', 'accounts.google.com', 'appleid.apple.com'];
const SEARCH_ENGINE_HOSTS: readonly RegExp[] = [
  /^(?:www\.)?google\.[a-z]{2,3}(?:\.[a-z]{2})?$/,
  /^(?:www\.)?yandex\.[a-z]{2,3}(?:\.[a-z]{2})?$/,
  /^(?:www\.)?ya\.ru$/,
  /^(?:www\.)?bing\.com$/,
  /^(?:www\.)?duckduckgo\.com$/,
  /^go\.mail\.ru$/,
  /^search\.yahoo\.com$/,
];
const CAMPAIGN_CAP_STEP = 10;
const ENCODED_VALUE_LIMIT_BYTES = 1000;

function isControlChar(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  return code < 0x20 || (code >= 0x7f && code <= 0x9f);
}

// Code points, not UTF-16 units: keeps a surrogate pair whole instead of leaving half of it in the data.
function sanitize(value: string | null | undefined, maxChars: number): string | undefined {
  if (!value) return undefined;
  const clean = Array.from(value)
    .filter(char => !isControlChar(char))
    .join('')
    .trim();
  const capped = Array.from(clean).slice(0, maxChars).join('').trim();
  return capped || undefined;
}

function referrerHost(referrer: string, pageHost: string): string | undefined {
  if (!referrer) return undefined;
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return undefined;
  }

  if (!host || SIGN_IN_HOSTS.includes(host) || host === pageHost.toLowerCase()) return undefined;
  return host;
}

function classify(host: string | undefined, utmSource: string | undefined): LumirEntrySource {
  if (utmSource) return 'campaign';
  if (!host) return 'direct';
  if (LANDING_HOSTS.includes(host)) return 'landing';
  if (host.endsWith(`.${PARENT_DOMAIN}`)) return 'product';
  if (SEARCH_ENGINE_HOSTS.some(pattern => pattern.test(host))) return 'search';
  return 'referral';
}

function withOptional(entry: LumirEntry, key: LumirEntryTextKey, value: string | undefined): LumirEntry {
  return value ? { ...entry, [key]: value } : entry;
}

export function buildLumirEntry(context: LumirEntryContext, now: Date = new Date()): LumirEntry {
  const params = new URLSearchParams(context.search);
  const host = referrerHost(context.referrer, context.hostname);
  const base = withOptional(
    { v: 1, src: classify(host, sanitize(params.get('utm_source'), LUMIR_ENTRY_TEXT_MAX_CHARS)), at: now.toISOString() },
    'ref',
    sanitize(host, LUMIR_ENTRY_TEXT_MAX_CHARS),
  );

  // Non-Latin utm values grow up to 12 bytes per character once encoded: shrink them until the cookie fits.
  for (let cap = LUMIR_ENTRY_TEXT_MAX_CHARS; cap > 0; cap -= CAMPAIGN_CAP_STEP) {
    let entry = withOptional(base, 'us', sanitize(params.get('utm_source'), cap));
    entry = withOptional(entry, 'um', sanitize(params.get('utm_medium'), cap));
    entry = withOptional(entry, 'uc', sanitize(params.get('utm_campaign'), cap));
    if (encodeURIComponent(JSON.stringify(entry)).length < ENCODED_VALUE_LIMIT_BYTES) return entry;
  }

  return base;
}

function hasValidEntry(): boolean {
  try {
    const raw = readCookie(LUMIR_ENTRY_COOKIE);
    if (!raw) return false;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return false;
    const { v, src } = parsed as { v?: unknown; src?: unknown };
    return v === 1 && (LUMIR_ENTRY_SOURCES as readonly unknown[]).includes(src);
  } catch {
    return false;
  }
}

function sharedCookieDomain(hostname: string): string | undefined {
  const host = hostname.toLowerCase();
  return host === PARENT_DOMAIN || host.endsWith(`.${PARENT_DOMAIN}`) ? `.${PARENT_DOMAIN}` : undefined;
}

export function recordLumirEntry(): void {
  try {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    if (hasValidEntry()) return;
    const { hostname, search } = window.location;
    const entry = buildLumirEntry({ referrer: document.referrer, search, hostname });
    writeCookie(LUMIR_ENTRY_COOKIE, JSON.stringify(entry), {
      maxAgeSeconds: ENTRY_MAX_AGE_SECONDS,
      domain: sharedCookieDomain(hostname),
    });
  } catch {
    // Cookies blocked: the visit stays unattributed, the app must still boot.
  }
}

export function provideLumirEntryCookie(): EnvironmentProviders {
  return provideAppInitializer(recordLumirEntry);
}
